// Wave RW.3 (2026-05-17) — ERP 웹 PROMISE 상세 + commit/complete/fail/cancel
// Wave RW.6 (2026-05-18) — 사진 lightbox (재활용 PhotoLightbox 컴포넌트)
// API: GET /api/rfq/inquiries/:id, POST :id/commit, :id/complete, :id/fail, :id/cancel
// autodex-app/app/rfq/[id].js (RN) React 포팅. 사진: /uploads/rfq/* 비인증 static.

import React, { useState, useEffect, useCallback } from 'react';
import { API, authFetch } from '../../utils/api';
import RFQCompleteModal from './RFQCompleteModal';
import RFQFailModal from './RFQFailModal';
import PhotoLightbox from '../PhotoLightbox';

var STATUS_LABEL = {
  open:      '입찰 모이는 중',
  committed: '매입 결정 (3일 reservation)',
  completed: '거래 완료',
  failed:    '매입 실패',
  cancelled: '취소',
  expired:   '만료',
};
var STATUS_COLOR = {
  open:      '#2563eb',
  committed: '#f59e0b',
  completed: '#10b981',
  failed:    '#ef4444',
  cancelled: '#94a3b8',
  expired:   '#94a3b8',
};

function formatKrw(n) {
  if (n == null) return '-';
  return (n / 10000).toFixed(0) + '만원';
}
function imgUrl(rel) {
  if (!rel) return null;
  if (typeof rel === 'object') rel = rel.url || rel.path;
  if (typeof rel !== 'string') return null;
  return rel.charAt(0) === '/' ? rel : '/' + rel;
}

// 2026-05-17 — 시세 제시 시간 상대 표시 (방금 / N분 전 / N시간 전 / N일 전 / 그 외 절대 날짜)
function formatRelativeTime(iso) {
  if (!iso) return '';
  var t = new Date(String(iso).replace(' ', 'T')).getTime();
  if (!t || isNaN(t)) return '';
  var diff = Date.now() - t;
  if (diff < 0) return '';
  var m = Math.floor(diff / 60000);
  if (m < 1)    return '방금';
  if (m < 60)   return m + '분 전';
  var h = Math.floor(m / 60);
  if (h < 24)   return h + '시간 전';
  var d = Math.floor(h / 24);
  if (d < 7)    return d + '일 전';
  // 그 외 — 절대 날짜 (M.D HH:mm)
  var dt = new Date(t);
  return (dt.getMonth() + 1) + '.' + dt.getDate() + ' ' + String(dt.getHours()).padStart(2, '0') + ':' + String(dt.getMinutes()).padStart(2, '0');
}

function RFQDetail(props) {
  var id = props.id;
  var showToast = props.showToast || function(){};
  var onBack = props.onBack || function(){};

  var [rfq, setRfq] = useState(null);
  var [loading, setLoading] = useState(true);
  var [error, setError] = useState('');
  var [actionLoading, setActionLoading] = useState(false);
  var [completeOpen, setCompleteOpen] = useState(false);
  var [failOpen, setFailOpen] = useState(false);
  var [viewIndex, setViewIndex] = useState(null);  // RW.6 lightbox

  var load = useCallback(function() {
    if (!id) return;
    setLoading(true);
    setError('');
    authFetch(API + '/rfq/inquiries/' + id)
      .then(function(r){ return r.json(); })
      .then(function(d) {
        if (d && d.success && d.data) {
          setRfq(d.data);
        } else {
          setError((d && d.error) || '조회 실패');
        }
        setLoading(false);
      })
      .catch(function(){ setError('네트워크 오류'); setLoading(false); });
  }, [id]);

  useEffect(function(){ load(); }, [load]);

  var onCommit = function(quote) {
    if (!window.confirm(formatKrw(quote.priceKrw) + ' 에 매입을 진행하시겠습니까?\n\n차량은 3일간 다른 거래가 차단됩니다.')) return;
    setActionLoading(true);
    authFetch(API + '/rfq/inquiries/' + id + '/commit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ quoteId: quote.id }),
    })
      .then(function(r){ return r.json(); })
      .then(function(d) {
        if (d && d.success) {
          showToast('매입 결정 완료 — 바이어에게 알림 전송됨', 'success');
          load();
        } else {
          showToast(friendlyError(d) || '매입 결정 실패', 'err');
        }
      })
      .catch(function(){ showToast('네트워크 오류', 'err'); })
      .finally(function(){ setActionLoading(false); });
  };

  var onCompleteSubmit = function(actualAmountKrw) {
    return authFetch(API + '/rfq/inquiries/' + id + '/complete', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ actualAmountKrw: actualAmountKrw }),
    })
      .then(function(r){ return r.json(); })
      .then(function(d) {
        if (d && d.success) {
          var manwon = (actualAmountKrw / 10000).toLocaleString();
          showToast('거래 완료 신고 (' + manwon + '만원) — 시세 데이터 반영', 'success');
          setCompleteOpen(false);
          load();
        } else {
          throw new Error(friendlyError(d) || '실패');
        }
      });
  };

  var onFailSubmit = function(code) {
    return authFetch(API + '/rfq/inquiries/' + id + '/fail', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ failedReason: code }),
    })
      .then(function(r){ return r.json(); })
      .then(function(d) {
        if (d && d.success) {
          var delta = d.failed && d.failed.mannerDelta;
          var temp = d.failed && d.failed.mannerTemperature;
          var msg = delta < 0
            ? '실패 보고 — 매너 ' + delta + '° (현재 ' + (temp != null ? Number(temp).toFixed(1) : '?') + '°)'
            : '실패 보고 완료 — 페널티 없음 (면책)';
          showToast(msg, 'success');
          setFailOpen(false);
          load();
        } else {
          throw new Error(friendlyError(d) || '실패');
        }
      });
  };

  var onCancel = function() {
    // Kyle 2026-05-17 — 입찰자 (active quote) 있으면 quota 차감 유지. 메시지 분기.
    var liveQuotes = (rfq && rfq.quotes ? rfq.quotes : []).filter(function(q){ return q.status === 'active'; });
    var hasBids = liveQuotes.length > 0;
    var confirmMsg = hasBids
      ? '출품을 취소하시겠습니까?\n\n페널티는 없지만 입찰자가 ' + liveQuotes.length + '건 있어 월 출품 횟수는 차감 유지됩니다.'
      : '출품을 취소하시겠습니까?\n\n페널티는 없으며 월 quota 환급됩니다.';
    if (!window.confirm(confirmMsg)) return;
    setActionLoading(true);
    authFetch(API + '/rfq/inquiries/' + id + '/cancel', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason: '단순 취소' }),
    })
      .then(function(r){ return r.json(); })
      .then(function(d) {
        if (d && d.success) {
          var msg = d.quotaRefunded
            ? '출품 취소 완료 — quota 환급'
            : '출품 취소 완료 — 입찰자 ' + (d.activeQuoteCountAtCancel || 0) + '건, quota 차감 유지';
          showToast(msg, 'success');
          // 취소 후 목록으로 자동 복귀
          setTimeout(onBack, 600);
        } else {
          showToast(friendlyError(d) || '취소 실패', 'err');
        }
      })
      .catch(function(){ showToast('네트워크 오류', 'err'); })
      .finally(function(){ setActionLoading(false); });
  };

  // ── render ──
  if (loading && !rfq) return <div style={{ padding: '60px 0', textAlign: 'center', color: '#94a3b8' }}>불러오는 중...</div>;
  if (error)            return <div style={{ padding: '60px 24px', textAlign: 'center' }}><div style={{ color: '#dc2626', marginBottom: 12 }}>{error}</div><button type="button" onClick={onBack} style={backBtnFull}>목록으로</button></div>;
  if (!rfq)             return null;

  var photos = Array.isArray(rfq.photos) ? rfq.photos : (rfq.photos && typeof rfq.photos === 'object' ? Object.values(rfq.photos) : []);
  // RW.6 lightbox adapter — PhotoLightbox 는 {url, memo} 객체 배열 가정
  var lightboxPhotos = photos.map(function(p){ return { url: imgUrl(p) }; }).filter(function(o){ return !!o.url; });
  var quotes = Array.isArray(rfq.quotes) ? rfq.quotes : [];
  var activeQuotes = quotes.filter(function(q){ return q.status === 'active'; });
  var winnerQuote = quotes.find(function(q){ return q.status === 'winner'; });
  var lostQuotes = quotes.filter(function(q){ return q.status === 'lost'; });

  return (
    <div style={{ padding: '20px 24px 80px', maxWidth: 1080, margin: '0 auto' }}>
      {/* 상태 헤더 */}
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', lineHeight: 1.3 }}>
              {rfq.carNo || '차량번호 미입력'}
            </div>
            <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>
              {(rfq.year ? rfq.year + ' · ' : '') + (rfq.model || '모델 미입력') + (rfq.mileage ? (' · ' + Number(rfq.mileage).toLocaleString() + ' km') : '')}
            </div>
          </div>
          <span style={statusBadge(rfq.status)}>{STATUS_LABEL[rfq.status] || rfq.status}</span>
        </div>
        {rfq.ownerConsented && (
          <div style={{ marginTop: 10, display: 'inline-block', background: '#ecfdf5', color: '#047857', padding: '4px 10px', borderRadius: 6, fontSize: 12, fontWeight: 700 }}>
            🛡 차주 사전 동의
          </div>
        )}
        {rfq.memo && (
          <div style={{ marginTop: 12, padding: '10px 12px', background: '#f8fafc', borderRadius: 8, fontSize: 13, color: '#475569', lineHeight: 1.5 }}>
            {rfq.memo}
          </div>
        )}
        <div style={{ marginTop: 12, fontSize: 11, color: '#94a3b8' }}>
          출품일 {rfq.createdAt} {rfq.expiresAt ? '· 만료 ' + rfq.expiresAt : ''}
        </div>
      </div>

      {/* 사진 — RW.6 클릭 시 lightbox */}
      <div style={card}>
        <div style={sectionTitle}>차량 사진 ({lightboxPhotos.length}장)</div>
        {lightboxPhotos.length === 0 ? (
          <div style={{ padding: '30px 0', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>사진 없음</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 10 }}>
            {lightboxPhotos.map(function(p, idx) {
              return (
                <div key={p.url + '_' + idx}
                     style={Object.assign({}, photoCell, { cursor: 'pointer' })}
                     onClick={function(){ setViewIndex(idx); }}
                     title="클릭해서 크게 보기">
                  <img src={p.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    onError={function(e){ try { e.target.style.display='none'; } catch(_e){} }} />
                </div>
              );
            })}
          </div>
        )}
      </div>
      <PhotoLightbox photos={lightboxPhotos} viewIndex={viewIndex} setViewIndex={setViewIndex} />

      {/* contactInfo (committed/completed 만 노출) */}
      {rfq.contactInfo && rfq.contactInfo.role === 'buyer' && (
        <div style={card}>
          <div style={sectionTitle}>📞 선택한 바이어 연락처</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>
            {rfq.contactInfo.name || '-'}
            {rfq.contactInfo.company && <span style={{ marginLeft: 6, fontSize: 12, fontWeight: 600, color: '#64748b' }}>({rfq.contactInfo.company})</span>}
          </div>
          {rfq.contactInfo.country && (
            <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>🌐 {rfq.contactInfo.country}</div>
          )}
          {rfq.contactInfo.phone && (
            <a href={'tel:' + rfq.contactInfo.phone} style={callBtn}>
              📞 {rfq.contactInfo.phone}
            </a>
          )}
          <div style={{ marginTop: 12, padding: 10, background: '#fef3c7', borderRadius: 6, fontSize: 11, color: '#92400e', lineHeight: 1.5 }}>
            매입 절차 조율 및 거래 진행을 위해 직접 연락 가능합니다. 외부 채널 이탈은 시장 룰 위반으로 매너 차감 사유입니다.
          </div>
        </div>
      )}

      {/* quotes 비교 */}
      <div style={card}>
        <div style={sectionTitle}>
          시세 비교 ({activeQuotes.length + (winnerQuote ? 1 : 0) + lostQuotes.length}건
          {rfq.status === 'open' && activeQuotes.length > 0 ? ' · 매입 결정 가능' : ''})
        </div>

        {rfq.status === 'open' && activeQuotes.length === 0 && (
          <div style={{ padding: '24px 0', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
            아직 시세가 제출되지 않았습니다 (48시간 만료 전까지 대기)
          </div>
        )}

        {/* winner 강조 */}
        {winnerQuote && (
          <div style={quoteRowWinner}>
            <div>
              <div style={{ fontSize: 11, color: '#10b981', fontWeight: 700 }}>✓ 선정된 시세 (Winner)</div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 2 }}>
                <div style={{ fontSize: 18, fontWeight: 800, color: '#0f172a' }}>{formatKrw(winnerQuote.priceKrw)}</div>
                {winnerQuote.createdAt && (
                  <div style={{ fontSize: 11, color: '#94a3b8' }} title={winnerQuote.createdAt}>
                    {formatRelativeTime(winnerQuote.createdAt)}
                  </div>
                )}
              </div>
              {winnerQuote.memo && <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>{winnerQuote.memo}</div>}
            </div>
          </div>
        )}

        {/* active quotes — 순번 (가격 내림차순) + 시간. open 상태에서만 [매입 결정] 버튼 */}
        {activeQuotes.map(function(q, idx) {
          return (
            <div key={q.id} style={quoteRow}>
              {/* 순번 뱃지 — #1 = 최고가 */}
              <div style={rankBadge(idx)}>#{idx + 1}</div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                  <div style={{ fontSize: 17, fontWeight: 800, color: '#1B3A5C' }}>{formatKrw(q.priceKrw)}</div>
                  {q.createdAt && (
                    <div style={{ fontSize: 11, color: '#94a3b8' }} title={q.createdAt}>
                      {formatRelativeTime(q.createdAt)}
                    </div>
                  )}
                </div>
                {q.memo && <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>{q.memo}</div>}
              </div>
              {rfq.status === 'open' && (
                <button type="button" onClick={function(){ onCommit(q); }} disabled={actionLoading} style={btnCommit(actionLoading)}>
                  매입 결정
                </button>
              )}
            </div>
          );
        })}

        {/* lost quotes — 회색 read-only */}
        {lostQuotes.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 700, marginBottom: 6 }}>탈락한 시세</div>
            {lostQuotes.map(function(q) {
              return (
                <div key={q.id} style={quoteRowLost}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                    <div style={{ fontSize: 13, color: '#94a3b8', textDecoration: 'line-through' }}>{formatKrw(q.priceKrw)}</div>
                    {q.createdAt && (
                      <div style={{ fontSize: 11, color: '#cbd5e1' }} title={q.createdAt}>
                        {formatRelativeTime(q.createdAt)}
                      </div>
                    )}
                  </div>
                  {q.memo && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{q.memo}</div>}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* 액션 버튼 — 상태별 */}
      <div style={{ display: 'flex', gap: 10, marginTop: 16, justifyContent: 'flex-end' }}>
        {rfq.status === 'open' && (
          <button type="button" onClick={onCancel} disabled={actionLoading} style={btnCancel(actionLoading)}>
            출품 취소
          </button>
        )}
        {rfq.status === 'committed' && (
          <>
            <button type="button" onClick={function(){ setFailOpen(true); }} disabled={actionLoading} style={btnFail(actionLoading)}>
              매입 실패
            </button>
            <button type="button" onClick={function(){ setCompleteOpen(true); }} disabled={actionLoading} style={btnComplete(actionLoading)}>
              거래 완료
            </button>
          </>
        )}
      </div>

      {completeOpen && <RFQCompleteModal onSubmit={onCompleteSubmit} onClose={function(){ setCompleteOpen(false); }} />}
      {failOpen && <RFQFailModal onSubmit={onFailSubmit} onClose={function(){ setFailOpen(false); }} />}
    </div>
  );
}

function friendlyError(d) {
  if (!d) return null;
  var code = d.errorCode;
  if (code === 'QUOTE_ID_REQUIRED') return '시세 선택 필수';
  if (code === 'RFQ_NOT_OPEN')      return '진행중 상태가 아닙니다';
  if (code === 'RFQ_EXPIRED')       return '만료된 출품입니다';
  if (code === 'QUOTE_NOT_ACTIVE')  return '활성 시세가 아닙니다';
  if (code === 'TRADE_AMOUNT_REQUIRED') return '실 거래액 입력 필수';
  if (code === 'RFQ_NOT_COMMITTED') return '매입 결정 상태가 아닙니다';
  if (code === 'INVALID_REASON')    return '유효하지 않은 사유';
  return d.error;
}

// ── styles ──
var card = {
  background: 'white', borderRadius: 12,
  border: '1px solid #e2e8f0', padding: 18,
  marginBottom: 14,
};
var sectionTitle = {
  fontSize: 15, fontWeight: 700, color: '#0f172a', marginBottom: 12,
};
var photoCell = {
  position: 'relative', aspectRatio: '4/3',
  borderRadius: 8, overflow: 'hidden',
  border: '1px solid #e2e8f0', background: '#f1f5f9',
};
var quoteRow = {
  display: 'flex', alignItems: 'center', gap: 12,
  padding: '12px 0',
  borderBottom: '1px solid #f1f5f9',
};
// 2026-05-17 — 시세 순번 뱃지 (가격 내림차순, #1 = 최고가)
function rankBadge(idx) {
  // #1 강조 (gold), #2 silver, #3 bronze, 그 외 회색
  var bg = idx === 0 ? '#fef3c7' : idx === 1 ? '#f1f5f9' : idx === 2 ? '#fce7d4' : '#f8fafc';
  var color = idx === 0 ? '#b45309' : idx === 1 ? '#475569' : idx === 2 ? '#9a3412' : '#94a3b8';
  return {
    minWidth: 32,
    padding: '4px 8px',
    background: bg,
    color: color,
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 800,
    textAlign: 'center',
    flexShrink: 0,
  };
}
var quoteRowWinner = {
  padding: '14px 16px',
  background: '#ecfdf5', border: '1.5px solid #10b981', borderRadius: 8,
  marginBottom: 10,
};
var quoteRowLost = {
  padding: '8px 12px',
  background: '#f8fafc', borderRadius: 6,
  marginBottom: 6,
};
function statusBadge(status) {
  return {
    background: STATUS_COLOR[status] || '#94a3b8', color: 'white',
    padding: '5px 12px', borderRadius: 6,
    fontSize: 12, fontWeight: 700,
    whiteSpace: 'nowrap',
  };
}
function btnCommit(disabled) {
  return {
    padding: '8px 14px',
    background: disabled ? '#cbd5e1' : '#1B3A5C',
    color: 'white', border: 'none', borderRadius: 8,
    fontSize: 13, fontWeight: 700,
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontFamily: "'Pretendard',sans-serif",
  };
}
function btnComplete(disabled) {
  return Object.assign({}, btnCommit(disabled), { background: disabled ? '#cbd5e1' : '#10b981', padding: '10px 18px', fontSize: 14 });
}
function btnFail(disabled) {
  return Object.assign({}, btnCommit(disabled), { background: 'white', color: '#dc2626', border: '1.5px solid #dc2626', padding: '10px 18px', fontSize: 14 });
}
function btnCancel(disabled) {
  return Object.assign({}, btnCommit(disabled), { background: 'white', color: '#64748b', border: '1.5px solid #e2e8f0', padding: '10px 18px', fontSize: 14 });
}
var callBtn = {
  display: 'inline-block', marginTop: 10,
  padding: '8px 14px',
  background: '#1B3A5C', color: 'white',
  textDecoration: 'none', borderRadius: 8,
  fontSize: 14, fontWeight: 700,
};
var backBtnFull = {
  padding: '10px 20px',
  background: 'white', border: '1.5px solid #e2e8f0', borderRadius: 8,
  fontSize: 14, fontWeight: 600, color: '#475569',
  cursor: 'pointer',
  fontFamily: "'Pretendard',sans-serif",
};

export default RFQDetail;
